import { Link } from "react-router-dom";
import { Breadcrumb } from "@/components/ui/breadcrumb";
import { NotAvailableState } from "@/components/ui/system-state";
import { PageHeader } from "@/components/data-display/page-header";

/**
 * Canonical shell for a page deferred at G2 (page-delivery-pipeline.md): a
 * backend that does not exist in this environment, rendered as an honest
 * NotAvailableState rather than fabricated data. Every deferred page uses
 * this instead of hand-composing PageHeader/Breadcrumb + NotAvailableState,
 * so the nav choice is made once and stays consistent as more pages defer.
 *
 * Nav rule: a top-level page (the route's own landing page, e.g. `/treasury`,
 * `/recovery`, `/admin/support-bundle`) gets `nav={{ kind: "header", ... }}`
 * — a PageHeader with the page's own title and subtitle. A sub-page reached
 * only via one or more parents (e.g. `/treasury/staking/new`, or
 * `/treasury/intent` reached via Treasury > Opportunity Queue) gets
 * `nav={{ kind: "breadcrumb", parents: [{label, href}, ...], label }}` — a
 * Breadcrumb back through its parent chain. This mirrors how the rest of
 * sandbox-shell already distinguishes a section landing page from a page
 * reached by drilling in.
 */
export function DeferredPage({
  nav,
  state,
  available,
}: {
  nav:
    | { kind: "header"; title: string; subtitle?: string }
    | { kind: "breadcrumb"; parents: { label: string; href: string }[]; label: string };
  state: {
    title: string;
    description?: string;
    reason?: string;
  };
  /** Working pages related to this one, shown as a "What's available now" list. */
  available?: { label: string; href: string; description?: string }[];
}) {
  return (
    <div>
      {nav.kind === "header" ? (
        <PageHeader title={nav.title} subtitle={nav.subtitle} />
      ) : (
        <Breadcrumb items={[...nav.parents, { label: nav.label }]} />
      )}
      <NotAvailableState title={state.title} description={state.description} reason={state.reason} />
      {available && available.length > 0 && (
        <section aria-labelledby="deferred-available-heading" className="mx-auto mt-6 max-w-[520px]">
          <h2 id="deferred-available-heading" className="mb-2 text-sm font-bold text-ink">What&apos;s available now</h2>
          <ul className="divide-y divide-line rounded-lg border border-line bg-surface">
            {available.map((item) => (
              <li key={item.href}>
                <Link to={item.href} className="block px-4 py-3 hover:bg-bg">
                  <span className="text-sm font-semibold text-primary">{item.label}</span>
                  {item.description && <span className="block text-xs text-muted">{item.description}</span>}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
